import i18n from "@/lib/i18n";
import { api, apiGet, apiPost, apiPut } from "@/lib/api";

export interface VapidStatus {
  configured: boolean;
  public_key: string | null;
  message?: string;
}

export interface PushSchedule {
  timezone: string;
  quiet_start: string | null;
  quiet_end: string | null;
  enabled: boolean;
}

export function getVapidStatus(): Promise<VapidStatus> {
  return apiGet<VapidStatus>("/v1/web-push/vapid-public-key");
}

export function listWebPushSubscriptions(): Promise<{ subscriptions: { id: string; endpoint: string }[] }> {
  return apiGet("/v1/web-push/subscriptions");
}

export function saveWebPushSubscription(subscription: PushSubscription): Promise<{ ok: boolean; configured: boolean }> {
  const json = subscription.toJSON();
  return apiPost("/v1/web-push/subscriptions", {
    endpoint: json.endpoint,
    expirationTime: json.expirationTime ?? null,
    keys: json.keys,
  });
}

export function deleteWebPushSubscription(endpoint: string): Promise<{ ok: boolean }> {
  return api("/v1/web-push/subscriptions", { method: "DELETE", body: JSON.stringify({ endpoint }) });
}

export function readPushSchedule(value: unknown): PushSchedule {
  const schedule = value as Partial<PushSchedule> | null;
  const time = (v: unknown) => v === null || (typeof v === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(v));
  if (!schedule || typeof schedule.timezone !== "string" || typeof schedule.enabled !== "boolean" || !time(schedule.quiet_start) || !time(schedule.quiet_end)) {
    throw new Error(i18n.t("profilePage.webPush.scheduleReadFailed"));
  }
  try { new Intl.DateTimeFormat("en-US", { timeZone: schedule.timezone }).format(); }
  catch { throw new Error(i18n.t("profilePage.webPush.invalidTimezone")); }
  return schedule as PushSchedule;
}

export async function getWebPushSchedule(): Promise<{ schedule: PushSchedule }> {
  const result = await apiGet<{ schedule?: unknown }>("/v1/web-push/schedule");
  return { schedule: readPushSchedule(result?.schedule) };
}

export async function saveWebPushSchedule(schedule: PushSchedule): Promise<{ schedule: PushSchedule }> {
  const result = await apiPut<{ schedule?: unknown }>("/v1/web-push/schedule", readPushSchedule(schedule));
  return { schedule: readPushSchedule(result?.schedule) };
}
